import { useState, Fragment, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useUser } from '@/shared/hooks/useAuth';
import { isSuperAdminOrOwner } from '@jira-clone/shared-types';
import { Plus, TrendingUp, RefreshCw, Users, ChevronDown, ChevronRight, Search, Trash2, CheckCircle2, Calculator, Sliders } from 'lucide-react';
import toast from 'react-hot-toast';
import { useHrSettings } from '../hooks/useHrSettings';

// ── Types ───────────────────────────────────────────────────────────────────

interface ClientDeal {
  dealId: string;
  clientName: string;
  dealTitle: string;
  dealStage: string;
  dealValue: number;
  targetAmount: number | null;
  revenueReceived: number;
  incentivePercent: number | null;
  incentiveAmount: number;
}

interface IncentiveTarget {
  _id: string;
  employeeId: { _id: string; display_name: string; department?: string };
  period: string;
  targetAmount: number;
  revenueGenerated: number;
  achievedPercent: number;
  incentivePercent: number;
  incentiveAmount: number;
  assignedBy: { _id: string; display_name: string };
  source: string;
  status: string;
  clientDeals: ClientDeal[];
}

interface DealItem {
  _id: string;
  title: string;
  client: string;
  stage: string;
  value: number;
  probability: number;
  priority: string;
  assignedToUserId: string | null;
  assignedToUserName?: string | null;
}

// ── Data hooks ──────────────────────────────────────────────────────────────

function useTargets(period?: string) {
  return useQuery({
    queryKey: ['hr', 'incentive-targets', period],
    queryFn: async () => {
      const p = period ? `?period=${period}` : '';
      const { data } = await api.get(`/api/hr-incentives/targets${p}`);
      return data as { data: IncentiveTarget[]; summary: { totalTarget: number; totalRevenue: number; totalIncentive: number } };
    },
  });
}

function useMyTargets() {
  return useQuery({
    queryKey: ['hr', 'my-targets'],
    queryFn: async () => {
      const { data } = await api.get('/api/hr-incentives/targets/mine');
      return data as { data: IncentiveTarget[] };
    },
  });
}

function isBdeOrSalesRepClient(e: { department?: string; departmentRole?: string; role?: string } | null | undefined): boolean {
  if (!e) return false;
  const dept = String(e.department ?? '').trim().toLowerCase();
  const role = String(e.departmentRole ?? e.role ?? '').trim().toLowerCase();
  if (dept === 'sales') return true;
  if (/\b(bde|bd|sales|business\s*development)\b/i.test(dept)) return true;
  if (/\b(bde|bd|sales|business\s*development)\b/i.test(role)) return true;
  return false;
}

function useEmployeeList() {
  return useQuery({
    queryKey: ['hr', 'employees-list', 'sales'],
    queryFn: async () => {
      const { data } = await api.get('/api/v2/hr/employees?limit=200&onlySales=true');
      const list = (data?.employees || data?.data || []) as {
        _id: string;
        display_name: string;
        department?: string;
        departmentRole?: string;
        isBde?: boolean;
      }[];
      return list.filter((e) => isBdeOrSalesRepClient(e));
    },
  });
}

function useDeals(employeeId?: string, all?: boolean, q?: string) {
  return useQuery({
    queryKey: ['hr', 'incentive-deals', employeeId, all, q],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (employeeId && !all) params.set('employeeId', employeeId);
      if (all) params.set('all', 'true');
      if (q) params.set('q', q);
      const { data } = await api.get(`/api/hr-incentives/deals?${params.toString()}`);
      return (data?.data || []) as DealItem[];
    },
    enabled: !!employeeId || !!all,
  });
}

function useAddClientDeals() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dealIds }: { id: string; dealIds: string[] }) => {
      const { data } = await api.post(`/api/hr-incentives/targets/${id}/client-deals`, { dealIds });
      return data;
    },
    onSuccess: (result: any) => {
      toast.success(result?.message || 'Client deals added and calculations updated.');
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] });
      qc.invalidateQueries({ queryKey: ['hr', 'my-targets'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Failed to add client deals.');
    },
  });
}

function useRemoveClientDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dealId }: { id: string; dealId: string }) => {
      const { data } = await api.delete(`/api/hr-incentives/targets/${id}/client-deals/${dealId}`);
      return data;
    },
    onSuccess: () => {
      toast.success('Client deal removed from target.');
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] });
      qc.invalidateQueries({ queryKey: ['hr', 'my-targets'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.response?.data?.error || 'Failed to remove client deal.');
    },
  });
}

function useAssignTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      employeeId: string;
      period: string;
      targetAmount: number;
      incentivePercent: number;
      source?: string;
      clientDeals?: { dealId: string; revenueReceived?: number; incentivePercent?: number }[];
    }) => {
      const { data } = await api.post('/api/hr-incentives/targets', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] });
    },
  });
}

function useUpdateRevenue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, revenueGenerated }: { id: string; revenueGenerated: number }) => {
      const { data } = await api.patch(`/api/hr-incentives/targets/${id}/revenue`, { revenueGenerated });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] });
    },
  });
}

function useUpdateClientPayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      dealId,
      revenueReceived,
      incentivePercent,
    }: {
      id: string;
      dealId: string;
      revenueReceived: number;
      incentivePercent?: number;
    }) => {
      const { data } = await api.patch(`/api/hr-incentives/targets/${id}/client-payment`, {
        dealId,
        revenueReceived,
        incentivePercent,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] });
      qc.invalidateQueries({ queryKey: ['hr', 'my-targets'] });
    },
  });
}

function useSyncCrmPayments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const { data } = await api.post(`/api/hr-incentives/targets/${id}/sync-crm-payments`);
      return data;
    },
    onSuccess: (result) => {
      if (result.synced) {
        toast.success(`Synced ₹${result.syncedAmount.toLocaleString('en-IN')} from CRM.`);
      } else {
        toast('No CRM payments found for this period.');
      }
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] });
      qc.invalidateQueries({ queryKey: ['hr', 'my-targets'] });
    },
  });
}

function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

// ── Helper ──────────────────────────────────────────────────────────────────

function stageBadgeClass(stage: string): string {
  const s = stage.toLowerCase();
  if (s.includes('won') || s.includes('closed')) return 'bg-emerald-100 text-emerald-800';
  if (s.includes('proposal') || s.includes('negotiation')) return 'bg-blue-100 text-blue-800';
  if (s.includes('lost') || s.includes('cancel')) return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-700';
}

// ── Deal Picker (multi-select inside the Assign modal) ──────────────────────

function DealPicker({ deals, selectedIds, onToggle }: {
  deals: DealItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  if (deals.length === 0) {
    return <p className="text-xs text-muted-foreground">No deals found for this employee.</p>;
  }
  return (
    <div className="max-h-48 overflow-auto rounded-md border">
      {deals.map((d) => (
        <label
          key={d._id}
          className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-muted/30 border-b last:border-0"
        >
          <input
            type="checkbox"
            checked={selectedIds.has(d._id)}
            onChange={() => onToggle(d._id)}
            className="accent-blue-600"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium truncate">{d.client}</span>
              <span className="text-xs text-muted-foreground truncate">— {d.title}</span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${stageBadgeClass(d.stage)}`}>{d.stage}</span>
              <span className="text-xs text-muted-foreground">₹{d.value.toLocaleString('en-IN')}</span>
            </div>
          </div>
        </label>
      ))}
    </div>
  );
}

// ── Add Client Deals Modal ──────────────────────────────────────────────────

function AddClientDealsModal({
  target,
  isOpen,
  onClose,
}: {
  target: IncentiveTarget | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [showAllDeals, setShowAllDeals] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const addMut = useAddClientDeals();

  const empId = target?.employeeId?._id;
  const { data: deals, isLoading } = useDeals(empId, showAllDeals, searchQuery);

  const existingDealIds = new Set((target?.clientDeals || []).map((cd) => String(cd.dealId)));

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearchQuery('');
      setShowAllDeals(false);
    }
  }, [isOpen, target?._id]);

  if (!target) return null;

  const toggleDeal = (dealId: string) => {
    if (existingDealIds.has(dealId)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one client deal to add.');
      return;
    }
    try {
      await addMut.mutateAsync({ id: target._id, dealIds: Array.from(selectedIds) });
      onClose();
    } catch {
      // Handled in mutation onError
    }
  };

  const repName = target.employeeId?.display_name || 'Sales Representative';

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`Add Clients to Target (${target.period})`}
      footer={
        <div className="flex items-center justify-between w-full">
          <span className="text-xs text-muted-foreground">
            {selectedIds.size} client deal(s) selected
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={addMut.isPending}>
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={addMut.isPending || selectedIds.size === 0}>
              {addMut.isPending ? 'Adding & Calculating...' : `Add Selected (${selectedIds.size})`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3.5 py-1">
        {/* Rep & Target Info Banner */}
        <div className="p-2.5 rounded-lg bg-muted/40 border text-xs flex flex-wrap items-center justify-between gap-2">
          <div>
            <span className="text-muted-foreground">Representative: </span>
            <strong className="text-foreground">{repName}</strong>
          </div>
          <div>
            <span className="text-muted-foreground">Target Period: </span>
            <strong className="text-foreground">{target.period}</strong>
          </div>
          <div>
            <span className="text-muted-foreground">Incentive Rate: </span>
            <strong className="text-purple-700">{target.incentivePercent}%</strong>
          </div>
        </div>

        {/* Filter and Search Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by client or deal title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md border border-input bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="flex rounded-md border bg-muted/30 p-0.5 text-xs font-medium shrink-0">
            <button
              type="button"
              onClick={() => setShowAllDeals(false)}
              className={`px-2.5 py-1 rounded transition-colors ${!showAllDeals ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Assigned to Rep
            </button>
            <button
              type="button"
              onClick={() => setShowAllDeals(true)}
              className={`px-2.5 py-1 rounded transition-colors ${showAllDeals ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              All CRM Clients
            </button>
          </div>
        </div>

        {/* Deals List */}
        <div className="max-h-72 overflow-y-auto rounded-lg border divide-y bg-background">
          {isLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !deals || deals.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">
              No deals found {showAllDeals ? 'in the CRM' : `assigned to ${repName}`}.
              {!showAllDeals && (
                <div className="mt-2">
                  <Button size="sm" variant="outline" onClick={() => setShowAllDeals(true)}>
                    Browse All CRM Clients
                  </Button>
                </div>
              )}
            </div>
          ) : (
            deals.map((d) => {
              const isAlready = existingDealIds.has(d._id);
              const isChecked = isAlready || selectedIds.has(d._id);

              return (
                <label
                  key={d._id}
                  className={`flex items-start gap-3 p-3 text-sm transition-colors ${
                    isAlready
                      ? 'bg-muted/30 opacity-70 cursor-not-allowed'
                      : 'cursor-pointer hover:bg-muted/40'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled={isAlready}
                    onChange={() => toggleDeal(d._id)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-foreground truncate">{d.client}</span>
                      <span className="text-xs font-semibold text-foreground shrink-0">
                        ₹{(d.value || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                      {d.title}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${stageBadgeClass(d.stage)}`}>
                        {d.stage}
                      </span>
                      {d.assignedToUserName && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                          Rep: {d.assignedToUserName}
                        </span>
                      )}
                      {isAlready && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-green-700 font-medium ml-auto">
                          <CheckCircle2 className="h-3 w-3" /> Already on Target
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })
          )}
        </div>

        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Adding a client will automatically link any payments received against that deal/client in {target.period} to this representative, calculate their incentive earned ({target.incentivePercent}%), and update their achievement totals.
        </p>
      </div>
    </Modal>
  );
}

// ── Update Client Revenue Modal ──────────────────────────────────────────────

function UpdateClientRevenueModal({
  target,
  initialDealId,
  isOpen,
  onClose,
}: {
  target: IncentiveTarget | null;
  initialDealId?: string | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const updatePaymentMut = useUpdateClientPayment();

  const deals = target?.clientDeals || [];
  const [selectedDealId, setSelectedDealId] = useState<string>('');
  const [revenueInput, setRevenueInput] = useState<string>('');
  const [incentivePctInput, setIncentivePctInput] = useState<string>('');

  useEffect(() => {
    if (!isOpen || !target) return;
    const dealToSelect =
      deals.find((d) => String(d.dealId) === String(initialDealId)) ||
      deals[0] ||
      null;

    if (dealToSelect) {
      setSelectedDealId(String(dealToSelect.dealId));
      setRevenueInput(String(dealToSelect.revenueReceived || 0));
      setIncentivePctInput(
        String(dealToSelect.incentivePercent ?? target.incentivePercent ?? 0)
      );
    } else {
      setSelectedDealId('');
      setRevenueInput('0');
      setIncentivePctInput(String(target.incentivePercent ?? 0));
    }
  }, [isOpen, target?._id, initialDealId]);

  const handleDealChange = (newDealId: string) => {
    setSelectedDealId(newDealId);
    const found = deals.find((d) => String(d.dealId) === String(newDealId));
    if (found) {
      setRevenueInput(String(found.revenueReceived || 0));
      setIncentivePctInput(String(found.incentivePercent ?? target?.incentivePercent ?? 0));
    }
  };

  if (!target) return null;

  const currentDeal = deals.find((d) => String(d.dealId) === String(selectedDealId)) || null;

  // Real-time calculations
  const enteredRevenue = Math.max(0, Number(revenueInput) || 0);
  const enteredPct = Math.min(100, Math.max(0, Number(incentivePctInput) || 0));
  const clientIncentiveEarned = Math.round(enteredRevenue * (enteredPct / 100));

  // Other deals totals on this target
  const otherDeals = deals.filter((d) => String(d.dealId) !== String(selectedDealId));
  const otherDealsRevenue = otherDeals.reduce((sum, d) => sum + (Number(d.revenueReceived) || 0), 0);
  const otherDealsIncentive = otherDeals.reduce((sum, d) => sum + (Number(d.incentiveAmount) || 0), 0);

  // Projected Sales Representative Totals
  const projectedRepRevenue = otherDealsRevenue + enteredRevenue;
  const targetGoal = Number(target.targetAmount) || 1;
  const projectedAchievedPercent = Math.round((projectedRepRevenue / targetGoal) * 100);
  const projectedRepIncentive = otherDealsIncentive + clientIncentiveEarned;

  const repName = target.employeeId?.display_name || 'Sales Representative';

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!selectedDealId) {
      toast.error('Please select a client deal to update.');
      return;
    }

    try {
      await updatePaymentMut.mutateAsync({
        id: target._id,
        dealId: selectedDealId,
        revenueReceived: enteredRevenue,
        incentivePercent: enteredPct,
      });
      toast.success(
        `Revenue for ${currentDeal?.clientName || 'client'} updated to ₹${enteredRevenue.toLocaleString('en-IN')}. Representative achievement is now ${projectedAchievedPercent}%.`
      );
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update client revenue.');
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Update Client Revenue & Incentive"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-muted-foreground">
            Projected Rep Achievement:{' '}
            <strong className={projectedAchievedPercent >= 100 ? 'text-green-600' : 'text-blue-600'}>
              {projectedAchievedPercent}%
            </strong>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose} disabled={updatePaymentMut.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updatePaymentMut.isPending || !selectedDealId}>
              {updatePaymentMut.isPending ? 'Calculating & Saving...' : 'Save & Calculate'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        {/* Sales Representative & Period Info */}
        <div className="p-3 rounded-lg bg-muted/40 border text-xs flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="text-muted-foreground">Representative: </span>
            <strong className="text-foreground">{repName}</strong>
          </div>
          <div>
            <span className="text-muted-foreground">Target Period: </span>
            <strong className="text-foreground">{target.period}</strong>
          </div>
          <div>
            <span className="text-muted-foreground">Target Goal: </span>
            <strong className="text-foreground">₹{target.targetAmount.toLocaleString('en-IN')}</strong>
          </div>
        </div>

        {/* Client Selection */}
        {deals.length === 0 ? (
          <div className="p-4 text-center border border-dashed rounded-lg text-sm text-muted-foreground">
            No clients currently assigned to this target.
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-foreground flex items-center justify-between mb-1">
                <span>Select Client for Revenue</span>
                <span className="text-[11px] font-normal text-muted-foreground">
                  {deals.length} client(s) on target
                </span>
              </label>
              <select
                value={selectedDealId}
                onChange={(e) => handleDealChange(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {deals.map((cd) => (
                  <option key={cd.dealId} value={cd.dealId}>
                    {cd.clientName} — {cd.dealTitle} (Current: ₹{(cd.revenueReceived || 0).toLocaleString('en-IN')})
                  </option>
                ))}
              </select>
            </div>

            {/* Selected Client Deal Details Card */}
            {currentDeal && (
              <div className="p-2.5 rounded-lg border bg-blue-50/50 dark:bg-blue-950/20 text-xs space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-blue-900 dark:text-blue-300">
                    {currentDeal.clientName}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${stageBadgeClass(currentDeal.dealStage)}`}>
                    {currentDeal.dealStage}
                  </span>
                </div>
                <div className="text-muted-foreground truncate">{currentDeal.dealTitle}</div>
                <div className="flex items-center justify-between text-muted-foreground pt-1 border-t border-blue-200/50 dark:border-blue-900/40">
                  <span>Deal Contract Value: <strong>₹{(currentDeal.dealValue || 0).toLocaleString('en-IN')}</strong></span>
                  <span>Previously Recorded: <strong className="text-green-700">₹{(currentDeal.revenueReceived || 0).toLocaleString('en-IN')}</strong></span>
                </div>
              </div>
            )}

            {/* Inputs: Revenue Received & Incentive Percent */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              <div>
                <label className="text-xs font-semibold text-foreground flex items-center justify-between mb-1">
                  <span>Revenue Received (₹)</span>
                  {currentDeal?.dealValue ? (
                    <button
                      type="button"
                      onClick={() => setRevenueInput(String(currentDeal.dealValue))}
                      className="text-[10px] text-blue-600 hover:underline"
                    >
                      Fill Deal Value
                    </button>
                  ) : null}
                </label>
                <div className="relative">
                  <span className="absolute left-2.5 top-2 text-xs text-muted-foreground">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={revenueInput}
                    onChange={(e) => setRevenueInput(e.target.value)}
                    placeholder="0"
                    className="w-full h-9 pl-7 pr-3 rounded-md border border-input bg-background text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 font-medium"
                  />
                </div>
                <span className="text-[10px] text-muted-foreground">
                  Actual amount collected for this client in {target.period}
                </span>
              </div>

              <div>
                <label className="text-xs font-semibold text-foreground mb-1 block">
                  Incentive Rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={incentivePctInput}
                  onChange={(e) => setIncentivePctInput(e.target.value)}
                  placeholder={String(target.incentivePercent)}
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 font-medium"
                />
                <span className="text-[10px] text-muted-foreground">
                  Default target rate: {target.incentivePercent}%
                </span>
              </div>
            </div>

            {/* Live Calculation Cards */}
            <div className="rounded-lg border bg-gradient-to-br from-muted/30 to-muted/70 p-3 space-y-3">
              <div className="flex items-center gap-1.5 text-xs font-bold text-foreground uppercase tracking-wider">
                <Calculator className="h-3.5 w-3.5 text-primary" />
                Live Calculation Breakdown
              </div>

              {/* Client-level calculation */}
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded bg-background border">
                  <div className="text-[11px] text-muted-foreground">Client Revenue</div>
                  <div className="text-sm font-semibold text-green-700 dark:text-green-400 mt-0.5">
                    ₹{enteredRevenue.toLocaleString('en-IN')}
                  </div>
                </div>
                <div className="p-2 rounded bg-background border">
                  <div className="text-[11px] text-muted-foreground">Client Incentive ({enteredPct}%)</div>
                  <div className="text-sm font-semibold text-purple-700 dark:text-purple-400 mt-0.5">
                    ₹{clientIncentiveEarned.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>

              {/* Sales Representative Target Level Impact */}
              <div className="p-2.5 rounded-md bg-background border space-y-2">
                <div className="text-xs font-semibold text-foreground flex items-center justify-between">
                  <span>Sales Representative Totals Impact</span>
                  <span className="text-[11px] font-normal text-muted-foreground">{repName}</span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs pt-1 border-t">
                  <div>
                    <div className="text-[10px] text-muted-foreground">Total Revenue</div>
                    <div className="text-xs font-medium text-muted-foreground line-through">
                      ₹{target.revenueGenerated?.toLocaleString('en-IN') || 0}
                    </div>
                    <div className="text-xs font-bold text-green-700 dark:text-green-400">
                      ₹{projectedRepRevenue.toLocaleString('en-IN')}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] text-muted-foreground">Achievement</div>
                    <div className="text-xs font-medium text-muted-foreground line-through">
                      {target.achievedPercent || 0}%
                    </div>
                    <div className={`text-xs font-bold ${projectedAchievedPercent >= 100 ? 'text-green-600' : 'text-blue-600'}`}>
                      {projectedAchievedPercent}%
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] text-muted-foreground">Total Incentive</div>
                    <div className="text-xs font-medium text-muted-foreground line-through">
                      ₹{target.incentiveAmount?.toLocaleString('en-IN') || 0}
                    </div>
                    <div className="text-xs font-bold text-purple-700 dark:text-purple-400">
                      ₹{projectedRepIncentive.toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>

                {/* Achievement Progress Bar */}
                <div className="pt-1">
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
                    <span>Progress to Goal (₹{target.targetAmount.toLocaleString('en-IN')})</span>
                    <span className="font-semibold">{projectedAchievedPercent}%</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        projectedAchievedPercent >= 100
                          ? 'bg-green-600'
                          : projectedAchievedPercent >= 50
                          ? 'bg-blue-600'
                          : 'bg-amber-500'
                      }`}
                      style={{ width: `${Math.min(100, Math.max(0, projectedAchievedPercent))}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Client Deal Breakdown Table (expandable row content) ─────────────────────

function ClientDealBreakdown({
  target,
  isAdmin,
  onAddClients,
  onUpdateRevenue,
}: {
  target: IncentiveTarget;
  isAdmin: boolean;
  onAddClients?: (target: IncentiveTarget) => void;
  onUpdateRevenue?: (target: IncentiveTarget, dealId?: string) => void;
}) {
  const removeClientDealMut = useRemoveClientDeal();
  const syncMut = useSyncCrmPayments();
  const deals = target.clientDeals || [];

  if (deals.length === 0) {
    return (
      <div className="px-4 py-6 text-center bg-muted/10 rounded-b-lg border-t">
        <Users className="h-7 w-7 mx-auto text-muted-foreground/40 mb-1.5" />
        <p className="text-sm font-medium text-foreground">No clients assigned to this target</p>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">
          Assign clients and deals for {target.employeeId?.display_name || 'this representative'} to track per-client revenue and calculate payouts.
        </p>
        {isAdmin && onAddClients && (
          <Button size="sm" onClick={() => onAddClients(target)} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Add Clients
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="px-4 py-3 bg-muted/10 rounded-b-lg border-t space-y-2">
      <div className="flex items-center justify-between pb-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Client Deals ({deals.length})
          </span>
          <span className="text-xs text-muted-foreground">
            • Revenue: <strong className="text-green-700">₹{(target.revenueGenerated || 0).toLocaleString('en-IN')}</strong>
          </span>
          <span className="text-xs text-muted-foreground">
            • Payout: <strong className="text-purple-700">₹{(target.incentiveAmount || 0).toLocaleString('en-IN')}</strong>
          </span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5">
            {onUpdateRevenue && deals.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 text-primary hover:text-primary"
                onClick={() => onUpdateRevenue(target, deals[0]?.dealId)}
                title="Update client revenue & incentive calculation"
              >
                <TrendingUp className="h-3 w-3" /> Update Revenue
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1"
              title="Sync milestone payments from CRM"
              disabled={syncMut.isPending}
              onClick={() => syncMut.mutateAsync({ id: target._id })}
            >
              <RefreshCw className={`h-3 w-3 ${syncMut.isPending ? 'animate-spin' : ''}`} /> Sync CRM
            </Button>
            {onAddClients && (
              <Button
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => onAddClients(target)}
              >
                <Plus className="h-3 w-3" /> Add Client
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="overflow-auto">
        <table className="w-full text-left text-sm bg-background rounded-md border">
          <thead className="text-xs uppercase bg-muted/50 border-b">
            <tr>
              <th className="px-3 py-2">Client</th>
              <th className="px-3 py-2">Deal</th>
              <th className="px-3 py-2">Stage</th>
              <th className="px-3 py-2 text-right">Deal Value</th>
              <th className="px-3 py-2 text-right">Payment Received</th>
              <th className="px-3 py-2 text-right">Incentive Earned</th>
              {isAdmin && <th className="px-3 py-2 text-right">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {deals.map((cd, idx) => (
              <tr key={idx} className="hover:bg-muted/20">
                <td className="px-3 py-2 font-medium">{cd.clientName}</td>
                <td className="px-3 py-2 text-muted-foreground">{cd.dealTitle}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${stageBadgeClass(cd.dealStage)}`}>
                    {cd.dealStage}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">₹{(cd.dealValue || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-right text-green-700 font-medium">₹{(cd.revenueReceived || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-right font-semibold text-purple-700">₹{(cd.incentiveAmount || 0).toLocaleString('en-IN')}</td>
                {isAdmin && (
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-primary hover:text-primary hover:bg-primary/10"
                        title={`Update revenue received from ${cd.clientName}`}
                        onClick={() => onUpdateRevenue?.(target, cd.dealId)}
                      >
                        <TrendingUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                        title={`Remove ${cd.clientName} from this target`}
                        onClick={() => {
                          if (confirm(`Remove ${cd.clientName} (${cd.dealTitle}) from this target?`)) {
                            removeClientDealMut
                              .mutateAsync({ id: target._id, dealId: cd.dealId })
                              .catch(() => toast.error('Failed to remove.'));
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────

export function IncentivesPage() {
  const user = useUser();
  const roleLower = String(user?.role ?? '').toLowerCase();
  const isAdmin =
    isSuperAdminOrOwner(user?.role) ||
    ['superadmin', 'admin', 'owner'].includes(roleLower) ||
    (String(user?.department ?? '').toLowerCase() === 'hr' && ['manager', 'team_lead'].includes(roleLower));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Incentives & Targets</h1>
          <p className="text-sm text-muted-foreground mt-1">Assign revenue targets, track achievement, and calculate incentive payouts based on performance.</p>
        </div>
        {isAdmin && (
          <Link to="/app/settings/hr/performance">
            <Button variant="outline" size="sm" className="gap-2">
              <Sliders className="h-4 w-4" />
              Incentive Rules & Settings
            </Button>
          </Link>
        )}
      </div>
      {isAdmin ? <AdminIncentiveView /> : <EmployeeIncentiveView />}
    </div>
  );
}

// ── Admin View ──────────────────────────────────────────────────────────────

function AdminIncentiveView() {
  const [period, setPeriod] = useState(getCurrentPeriod());
  const { data, isLoading } = useTargets(period);
  const { data: employees } = useEmployeeList();
  const { data: hrSettings } = useHrSettings();
  const assignMut = useAssignTarget();
  const updateRevMut = useUpdateRevenue();
  const syncMut = useSyncCrmPayments();
  const [showNew, setShowNew] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [incentivePercent, setIncentivePercent] = useState('');
  const [selectedDealIds, setSelectedDealIds] = useState<Set<string>>(new Set());
  const { data: availableDeals, isLoading: dealsLoading } = useDeals(employeeId);
  const [expandedTargetId, setExpandedTargetId] = useState<string | null>(null);
  const [managingClientsTarget, setManagingClientsTarget] = useState<IncentiveTarget | null>(null);
  const [updatingRevenueTarget, setUpdatingRevenueTarget] = useState<IncentiveTarget | null>(null);
  const [updatingRevenueDealId, setUpdatingRevenueDealId] = useState<string | null>(null);

  const handleToggleDeal = (id: string) => {
    setSelectedDealIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleAssign = async () => {
    if (!employeeId || !targetAmount || !incentivePercent) {
      toast.error('All required fields must be filled.');
      return;
    }
    const payload: any = {
      employeeId,
      period,
      targetAmount: Number(targetAmount),
      incentivePercent: Number(incentivePercent),
      source: 'manual',
    };
    if (selectedDealIds.size > 0) {
      payload.clientDeals = [...selectedDealIds].map((dealId) => ({
        dealId,
        revenueReceived: 0,
      }));
    }
    try {
      await assignMut.mutateAsync(payload);
      toast.success('Target assigned.');
      setShowNew(false);
      setEmployeeId('');
      setTargetAmount('');
      setIncentivePercent('');
      setSelectedDealIds(new Set());
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed.');
    }
  };

  if (isLoading) return <Skeleton className="h-[400px]" />;
  const allTargets = data?.data || [];
  const targets = allTargets.filter((t) => isBdeOrSalesRepClient(t.employeeId));
  const summary = data?.summary || { totalTarget: 0, totalRevenue: 0, totalIncentive: 0 };

  return (
    <>
      <Modal
        open={showNew}
        onClose={() => setShowNew(false)}
        title="Assign Target to BDE / Sales Representative"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assignMut.isPending || !employeeId}>Assign</Button>
          </div>
        }
      >
        <div className="space-y-3 py-3">
          <Select
            name="t-employee"
            label="Sales Representative (BDE / Sales Rep)"
            value={employeeId}
            onChange={(e) => {
              setEmployeeId(e.target.value);
              setSelectedDealIds(new Set());
            }}
            options={[
              { value: '', label: 'Select BDE / Sales Representative...' },
              ...(employees || []).map((e) => ({
                value: e._id,
                label: `${e.display_name} (${e.departmentRole || e.department || 'Sales'})`,
              })),
            ]}
          />
          {(!employees || employees.length === 0) && (
            <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 p-2.5 rounded border border-amber-200 dark:border-amber-900 leading-relaxed">
              No BDE or Sales representatives found. Only employees with department set to &quot;Sales&quot; or role set to BDE / Business Development are eligible for sales targets and incentives.
            </p>
          )}
          {employeeId && (
            <div>
              <label className="mb-1.5 block text-sm font-medium">Select Clients / Deals</label>
              {dealsLoading ? (
                <Skeleton className="h-20" />
              ) : (
                <DealPicker
                  deals={availableDeals || []}
                  selectedIds={selectedDealIds}
                  onToggle={handleToggleDeal}
                />
              )}
              {selectedDealIds.size > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {selectedDealIds.size} deal(s) selected
                </p>
              )}
            </div>
          )}
          <Input
            name="t-target"
            label={`Target Amount for ${period} (₹)`}
            type="number"
            min="0"
            value={targetAmount}
            onChange={(e) => setTargetAmount(e.target.value)}
            placeholder="e.g. 500000"
          />
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-foreground">Incentive %</label>
              <Link
                to="/app/settings/hr/performance"
                className="text-[11px] text-primary hover:underline font-medium"
                target="_blank"
                rel="noreferrer"
              >
                Configure defaults →
              </Link>
            </div>
            <Input
              name="t-percent"
              type="number"
              min="0"
              max="100"
              value={incentivePercent}
              onChange={(e) => setIncentivePercent(e.target.value)}
              placeholder={`Default: ${hrSettings?.defaultIncentivePercent ?? 5}%`}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Incentive = Revenue Generated × Incentive%. Target determines goal tracking.
            Client deals track per-client revenue and incentive breakdown.
          </p>
        </div>
      </Modal>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border p-4 bg-blue-50">
          <p className="text-xs text-muted-foreground">Total Target</p>
          <p className="text-xl font-bold">₹{summary.totalTarget.toLocaleString('en-IN')}</p>
        </div>
        <div className="rounded-lg border p-4 bg-green-50">
          <p className="text-xs text-muted-foreground">Revenue Generated</p>
          <p className="text-xl font-bold text-green-700">₹{summary.totalRevenue.toLocaleString('en-IN')}</p>
        </div>
        <div className="rounded-lg border p-4 bg-purple-50">
          <p className="text-xs text-muted-foreground">Incentive Payout</p>
          <p className="text-xl font-bold text-purple-700">₹{summary.totalIncentive.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Period selector + assign button */}
      <div className="flex items-center gap-3">
        <Input
          name="period"
          label="Period"
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="w-44"
        />
        <Button
          size="sm"
          onClick={() => {
            if (!incentivePercent && hrSettings?.defaultIncentivePercent != null) {
              setIncentivePercent(String(hrSettings.defaultIncentivePercent));
            }
            setShowNew(true);
          }}
          className="mt-5"
        >
          <Plus className="h-4 w-4 mr-1" />Assign Target
        </Button>
      </div>

      {/* Targets Table */}
      <Card className="border-none shadow-sm">
        <CardHeader title="Employee Targets & Achievement" />
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[450px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr>
                  <th className="w-8 px-2 py-3"></th>
                  <th className="px-4 py-3">Representative (BDE / Sales)</th>
                  <th className="px-4 py-3 text-right">Target</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-4 py-3 text-right">Achievement</th>
                  <th className="px-4 py-3 text-right">Incentive %</th>
                  <th className="px-4 py-3 text-right">Payout</th>
                  <th className="px-4 py-3">Clients</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {targets.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">
                      No targets assigned for this period.
                    </td>
                  </tr>
                )}
                {targets.map((t) => {
                  const isExpanded = expandedTargetId === t._id;
                  const clientCount = (t.clientDeals || []).length;
                  return (
                    <Fragment key={t._id}>
                      <tr className="hover:bg-muted/20 cursor-pointer" onClick={() => setExpandedTargetId(isExpanded ? null : t._id)}>
                        <td className="px-2 py-3">
                          {clientCount > 0 ? (
                            isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                          ) : null}
                        </td>
                        <td className="px-4 py-3 font-medium">{t.employeeId?.display_name || '-'}</td>
                        <td className="px-4 py-3 text-right">₹{t.targetAmount?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-right font-medium text-green-700">₹{t.revenueGenerated?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`font-bold ${t.achievedPercent >= 100 ? 'text-green-600' : t.achievedPercent >= 70 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {t.achievedPercent?.toFixed(0)}%
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{t.incentivePercent}%</td>
                        <td className="px-4 py-3 text-right font-semibold text-purple-700">₹{t.incentiveAmount?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1.5">
                            {clientCount > 0 ? (
                              <button
                                type="button"
                                onClick={() => setExpandedTargetId(isExpanded ? null : t._id)}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/60 dark:text-blue-300 text-xs font-medium transition-colors cursor-pointer"
                                title="Click to view client breakdown"
                              >
                                <Users className="h-3 w-3" />{clientCount}
                              </button>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setManagingClientsTarget(t);
                                setExpandedTargetId(t._id);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-dashed border-blue-400 hover:border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-xs font-medium transition-colors"
                              title={`Add clients assigned to ${t.employeeId?.display_name || 'this representative'}`}
                            >
                              <Plus className="h-3 w-3" /> Add
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              title={clientCount > 0 ? "Update client revenue & incentive calculation" : "Update revenue"}
                              onClick={() => {
                                if (clientCount > 0) {
                                  setUpdatingRevenueTarget(t);
                                  setUpdatingRevenueDealId(t.clientDeals?.[0]?.dealId || null);
                                  setExpandedTargetId(t._id);
                                } else {
                                  const rev = prompt(
                                    `Enter current revenue for ${t.employeeId?.display_name}:`,
                                    String(t.revenueGenerated || 0)
                                  );
                                  if (rev != null) {
                                    updateRevMut
                                      .mutateAsync({ id: t._id, revenueGenerated: Number(rev) })
                                      .then(() => toast.success('Revenue updated.'))
                                      .catch(() => toast.error('Failed.'));
                                  }
                                }
                              }}
                            >
                              <TrendingUp className="h-3.5 w-3.5" />
                            </Button>
                            {clientCount > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                title="Sync from CRM"
                                disabled={syncMut.isPending}
                                onClick={() => syncMut.mutateAsync({ id: t._id }).catch(() => toast.error('Sync failed.'))}
                              >
                                <RefreshCw className={`h-3.5 w-3.5 ${syncMut.isPending ? 'animate-spin' : ''}`} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} className="p-0">
                            <ClientDealBreakdown
                              target={t}
                              isAdmin={true}
                              onAddClients={(target) => setManagingClientsTarget(target)}
                              onUpdateRevenue={(target, dealId) => {
                                setUpdatingRevenueTarget(target);
                                setUpdatingRevenueDealId(dealId || null);
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Modal to add clients to an existing target */}
      <AddClientDealsModal
        target={managingClientsTarget}
        isOpen={!!managingClientsTarget}
        onClose={() => setManagingClientsTarget(null)}
      />

      {/* Modal to update client revenue & incentive calculation */}
      <UpdateClientRevenueModal
        target={updatingRevenueTarget}
        initialDealId={updatingRevenueDealId}
        isOpen={!!updatingRevenueTarget}
        onClose={() => {
          setUpdatingRevenueTarget(null);
          setUpdatingRevenueDealId(null);
        }}
      />
    </>
  );
}

// ── Employee View ───────────────────────────────────────────────────────────

function EmployeeIncentiveView() {
  const { data, isLoading } = useMyTargets();
  const [expandedTargetId, setExpandedTargetId] = useState<string | null>(null);

  if (isLoading) return <Skeleton className="h-[300px]" />;
  const targets = data?.data || [];

  return (
    <Card className="border-none shadow-sm">
      <CardHeader title="My Targets & Incentives" />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[500px]">
          {targets.length === 0 ? (
            <p className="px-4 py-8 text-center text-muted-foreground">
              No targets assigned to you yet.
            </p>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr>
                  <th className="w-8 px-2 py-3"></th>
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3 text-right">Target</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-4 py-3 text-right">Achievement</th>
                  <th className="px-4 py-3 text-right">Incentive Earned</th>
                  <th className="px-4 py-3">Clients</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {targets.map((t) => {
                  const isExpanded = expandedTargetId === t._id;
                  const clientCount = (t.clientDeals || []).length;
                  return (
                    <Fragment key={t._id}>
                      <tr className="hover:bg-muted/20 cursor-pointer" onClick={() => setExpandedTargetId(isExpanded ? null : t._id)}>
                        <td className="px-2 py-3">
                          {clientCount > 0 ? (
                            isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                          ) : null}
                        </td>
                        <td className="px-4 py-3 font-medium">{t.period}</td>
                        <td className="px-4 py-3 text-right">₹{t.targetAmount?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-right text-green-700">₹{t.revenueGenerated?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-20 h-2 rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${t.achievedPercent >= 100 ? 'bg-green-500' : t.achievedPercent >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                                style={{ width: `${Math.min(100, t.achievedPercent)}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{t.achievedPercent?.toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-purple-700">₹{t.incentiveAmount?.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3">
                          {clientCount > 0 ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
                              <Users className="h-3 w-3" />{clientCount}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={7} className="bg-muted/10">
                            <ClientDealBreakdown target={t} isAdmin={false} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
